/**
 * Ce que l'écran de programmation lit et écrit, en un seul endroit.
 *
 * ## Rien ici ne décide d'un prix
 *
 * `programmer` transmet le tarif d'une semaine PLEINE ; c'est
 * `programme_programmer`, en base, qui proratise et qui écrit. L'écran affiche
 * un aperçu (`../programmation`), et le compte rendu montre ce que la base a
 * réellement inscrit.
 *
 * C'est délibéré, et c'est la leçon du défaut que `create_order` a fermé :
 * l'ancien `create-invoice` écrivait le prix que le navigateur lui envoyait, et
 * 221 inscriptions ont été facturées à un tarif qui existait mais n'était pas
 * le leur. Un écran qui calcule un prix est un écran qui peut le calculer
 * autrement que la facture.
 *
 * La seule règle que l'écran porte en propre est le fuseau de l'heure
 * d'ouverture (`./ouverture`), parce qu'elle concerne la SAISIE et non l'argent.
 */

import { supabase } from '../supabase';
import { versInstant } from './ouverture';

/** Une période de stage : cinq lignes identitaires, sans année ni dates. */
export interface Periode {
  id: string;
  code: string;
  label: string;
  ordre: number;
}

/** Une semaine de congé déclarée. */
export interface Semaine {
  id: string;
  period_id: string;
  annee: number;
  libelle: string;
  debut: string;
  fin: string;
  jours_ouverts: number;
  jours_reference: number;
  remarques: string | null;
  updated_at: string;
}

/** Une session déjà programmée, telle que `programme_sessions` la rend. */
export interface SessionProgrammee {
  session_id: string;
  semaine_id: string;
  semaine: string;
  debut: string;
  fin: string;
  jours_ouverts: number;
  jours_reference: number;
  center_id: string;
  centre: string;
  stage_id: string;
  stage: string;
  capacite: number;
  /** Compté pour de vrai, jamais lu sur `current_registrations`. */
  inscrits: number;
  prix_normal: number;
  prix_reduit: number | null;
  prix_local: number | null;
  prix_local_reduit: number | null;
  tarif_condition_id: string | null;
  tarif_condition: string | null;
  archived: boolean;
  visible_from: string | null;
}

/** Un jeu de prix réellement pratiqué, et les centres qu'il couvrait. */
export interface TarifPratique {
  stage_id: string;
  stage: string;
  prix_normal: number;
  prix_reduit: number | null;
  prix_local: number | null;
  prix_local_reduit: number | null;
  tarif_condition_id: string | null;
  tarif_condition: string | null;
  capacite_mediane: number | null;
  center_ids: string[];
  centres: string[];
  nb_sessions: number;
}

/** Ce qu'un geste demande. */
export interface Geste {
  stage_id: string;
  center_ids: string[];
  semaine_ids: string[];
  /** Le tarif d'une semaine PLEINE. La base proratise les semaines écourtées. */
  prix_normal: number;
  prix_reduit?: number | null;
  prix_local?: number | null;
  prix_local_reduit?: number | null;
  tarif_condition_id?: string | null;
  capacite?: number | null;
  /** Heure de MUR — convertie ici, une fois, dans le fuseau de Bruxelles. */
  ouverture?: string | null;
}

/** Le compte rendu d'une session programmée. */
export interface ResultatDeGeste {
  session_id: string;
  semaine: string;
  centre: string;
  stage: string;
  action: 'creee' | 'mise_a_jour';
  capacite: number;
  jours_ouverts: number;
  prix_normal: number;
  prix_plein: number;
  /** Ce que la base a tenu à dire : tarif changé, session archivée, local sans condition. */
  message: string | null;
}

/**
 * Une erreur portant la phrase de la base.
 *
 * Les fonctions `programme_*` rendent des messages français adressés à
 * l'administration — « la capacité demandée (0) est sous les 12 inscrit(s) de
 * Berchem - Saint-Albert / Les Poussins en S1 ». Les masquer derrière un
 * « une erreur est survenue » ferait perdre exactement ce qui sert.
 */
export class ErreurDeProgrammation extends Error {
  readonly brut: string;
  constructor(brut: string | null | undefined) {
    const nettoye = (brut ?? '').trim();
    super(nettoye || 'La programmation a échoué, sans message.');
    this.brut = nettoye;
    this.name = 'ErreurDeProgrammation';
  }
}

/** Les cinq périodes, dans leur ordre d'affichage. */
export async function periodes(): Promise<Periode[]> {
  const { data, error } = await supabase
    .from('stage_periods')
    .select('id, code, label, ordre')
    .order('ordre')
    .order('label');
  if (error) throw new ErreurDeProgrammation(error.message);
  return (data ?? []) as Periode[];
}

/** Les années déjà programmées, plus l'année courante et la suivante. */
export async function anneesConnues(): Promise<number[]> {
  const { data, error } = await supabase
    .from('programme_semaines')
    .select('annee')
    .order('annee', { ascending: false });
  if (error) throw new ErreurDeProgrammation(error.message);

  const courante = new Date().getFullYear();
  const toutes = new Set<number>([courante, courante + 1]);
  (data ?? []).forEach((l: { annee: number }) => toutes.add(l.annee));
  return [...toutes].sort((a, b) => b - a);
}

// ---------------------------------------------------------------------------
// Les semaines
// ---------------------------------------------------------------------------

export async function semainesDeLaPeriode(
  periodId: string,
  annee: number,
): Promise<Semaine[]> {
  const { data, error } = await supabase
    .from('programme_semaines')
    .select('*')
    .eq('period_id', periodId)
    .eq('annee', annee)
    .order('debut');
  if (error) throw new ErreurDeProgrammation(error.message);
  return (data ?? []) as Semaine[];
}

/**
 * Crée ou met à jour une semaine.
 *
 * `jours_ouverts` n'est PAS envoyé quand il est laissé libre : le déclencheur
 * de la base le déduit du calendrier et des jours fériés. C'est la seule
 * définition de cette règle dans le dépôt, et l'écran ne la réimplémente pas —
 * il lit ce que la base a calculé.
 */
export async function enregistrerSemaine(semaine: {
  id?: string;
  period_id: string;
  annee: number;
  libelle: string;
  debut: string;
  fin: string;
  jours_reference?: number;
  /** Renseigné seulement pour contredire le calendrier (fermeture locale). */
  jours_ouverts?: number | null;
  remarques?: string | null;
}): Promise<Semaine> {
  const charge = {
    period_id: semaine.period_id,
    annee: semaine.annee,
    libelle: semaine.libelle.trim(),
    debut: semaine.debut,
    fin: semaine.fin,
    jours_reference: semaine.jours_reference ?? 5,
    jours_ouverts: semaine.jours_ouverts ?? null,
    remarques: semaine.remarques?.trim() || null,
  };

  const requete = semaine.id
    ? supabase.from('programme_semaines').update(charge).eq('id', semaine.id)
    : supabase.from('programme_semaines').insert(charge);

  const { data, error } = await requete.select('*').single();
  if (error) throw new ErreurDeProgrammation(error.message);
  return data as Semaine;
}

/**
 * Supprime une semaine.
 *
 * `sessions.semaine_id` est en `ON DELETE SET NULL` : les sessions déjà
 * programmées survivent et gardent leurs dates. Elles perdent seulement leur
 * rattachement — donc l'écran ne les reconnaîtra plus, et un nouveau geste en
 * créerait de nouvelles. L'écran prévient avant.
 */
export async function supprimerSemaine(id: string): Promise<void> {
  const { error } = await supabase.from('programme_semaines').delete().eq('id', id);
  if (error) throw new ErreurDeProgrammation(error.message);
}

/** Combien de sessions une semaine porte déjà, et combien d'inscrits. */
export async function empriseDeLaSemaine(
  id: string,
): Promise<{ sessions: number; inscrits: number }> {
  const { data, error } = await supabase
    .from('sessions')
    .select('id, current_registrations')
    .eq('semaine_id', id);
  if (error) throw new ErreurDeProgrammation(error.message);

  const lignes = (data ?? []) as { id: string; current_registrations: number }[];
  return {
    sessions: lignes.length,
    inscrits: lignes.reduce((t, l) => t + (l.current_registrations || 0), 0),
  };
}

/**
 * Les jours ouvrés d'un intervalle, demandés à la base.
 *
 * L'aperçu du formulaire de semaine ne recalcule pas cette règle :
 * `programme_jours_ouvres` en est la seule définition du dépôt, et elle dépend
 * de `public_holidays` — qu'une copie côté navigateur devrait charger et tenir
 * à jour pour dire la même chose.
 */
export async function joursOuvres(debut: string, fin: string): Promise<number | null> {
  if (!debut || !fin || fin < debut) return null;
  const { data, error } = await supabase.rpc('programme_jours_ouvres', {
    p_debut: debut,
    p_fin: fin,
  });
  if (error) throw new ErreurDeProgrammation(error.message);
  return typeof data === 'number' ? data : null;
}

// ---------------------------------------------------------------------------
// Programmer
// ---------------------------------------------------------------------------

/**
 * Un geste : un stage, N centres, N semaines, un jeu de prix, une capacité.
 *
 * Huit sessions pour « les Poussins dans quatre centres sur deux semaines », en
 * une transaction. Le cas particulier — un cinquième centre avec son tarif
 * local — est un second appel, pas une exception à saisir à la main.
 */
export async function programmer(geste: Geste): Promise<ResultatDeGeste[]> {
  const { data, error } = await supabase.rpc('programme_programmer', {
    p_stage_id: geste.stage_id,
    p_center_ids: geste.center_ids,
    p_semaine_ids: geste.semaine_ids,
    p_prix_normal: geste.prix_normal,
    p_prix_reduit: geste.prix_reduit ?? null,
    p_prix_local: geste.prix_local ?? null,
    p_prix_local_reduit: geste.prix_local_reduit ?? null,
    p_tarif_condition_id: geste.tarif_condition_id || null,
    p_capacite: geste.capacite ?? null,
    p_visible_from: versInstant(geste.ouverture),
  });
  if (error) throw new ErreurDeProgrammation(error.message);
  return (data ?? []) as ResultatDeGeste[];
}

/** Ce qui est déjà programmé pour une (période, année). */
export async function sessionsProgrammees(
  periodId: string,
  annee: number,
): Promise<SessionProgrammee[]> {
  const { data, error } = await supabase.rpc('programme_sessions', {
    p_period_id: periodId,
    p_annee: annee,
  });
  if (error) throw new ErreurDeProgrammation(error.message);
  return (data ?? []) as SessionProgrammee[];
}

/**
 * Les tarifs réellement pratiqués, groupés par jeu de prix.
 *
 * C'est ce qui rend « reprendre l'automne dernier » utile : la période rend
 * typiquement deux lignes par stage — « 84/44 dans quatre centres » et
 * « 100/49/39/39 à Watermael sous condition » — soit exactement les deux gestes
 * à rejouer.
 *
 * Lu sur les SESSIONS et non sur une table d'intentions : ce qui a été facturé
 * ne se discute pas.
 */
export async function tarifsPratiques(
  periodId: string,
  annee: number,
  stageId?: string | null,
): Promise<TarifPratique[]> {
  const { data, error } = await supabase.rpc('programme_tarifs_pratiques', {
    p_period_id: periodId,
    p_annee: annee,
    p_stage_id: stageId || null,
  });
  if (error) throw new ErreurDeProgrammation(error.message);
  return (data ?? []) as TarifPratique[];
}

/**
 * Corrige une session à l'unité — les places, le plus souvent.
 *
 * « Si je veux augmenter les places ici ou là je peux, au cas par cas. » Le
 * geste programme la généralité ; celui-ci rattrape l'exception sans rejouer
 * tout un centre.
 *
 * Ne touche ni aux dates ni à la semaine : elles viennent de la semaine
 * déclarée, et les changer ici les ferait diverger des autres sessions de la
 * même semaine — le défaut que tout ce chantier existe pour fermer.
 */
/**
 * Ce qu'une correction change — et ce qu'elle laisse tranquille.
 *
 * La distinction qui compte : **absent** veut dire « n'y touche pas »,
 * **`null`** veut dire « mets-le à vide ». Les deux sont des états légitimes —
 * un tarif réduit peut ne pas exister, une ouverture vide veut dire « visible
 * tout de suite » — donc `null` ne peut pas servir à dire « laisse ».
 *
 * C'est ce que les drapeaux `p_changer_*` de la fonction expriment côté base ;
 * ici, la présence de la clé suffit.
 */
export interface Correction {
  /** Présent = on change l'ouverture. `null` = visible dès la publication. */
  visible_from?: string | null;
  capacite?: number;
  tarifs?: {
    prix_normal: number;
    prix_reduit: number | null;
    prix_local: number | null;
    prix_local_reduit: number | null;
  };
  /** Présent = on change la condition. `null` = aucune. */
  tarif_condition_id?: string | null;
}

export interface SessionCorrigee {
  session_id: string;
  semaine: string;
  centre: string;
  stage: string;
  message: string;
}

/**
 * Corriger N sessions en une transaction.
 *
 * Passe par `programme_corriger` et non par un UPDATE : les trois inégalités
 * d'un rabais et la comparaison aux inscrits COMPTÉS y vivent, une fois, et le
 * refus tombe avant la première écriture. Corriger 45 ouvertures par 45
 * requêtes, ce serait 45 occasions de s'arrêter au milieu.
 */
export async function corriger(
  sessionIds: string[],
  correction: Correction,
): Promise<SessionCorrigee[]> {
  const { data, error } = await supabase.rpc('programme_corriger', {
    p_session_ids: sessionIds,
    p_changer_ouverture: 'visible_from' in correction,
    p_visible_from: correction.visible_from ?? null,
    p_changer_capacite: correction.capacite !== undefined,
    p_capacite: correction.capacite ?? null,
    p_changer_tarifs: correction.tarifs !== undefined,
    p_prix_normal: correction.tarifs?.prix_normal ?? null,
    p_prix_reduit: correction.tarifs?.prix_reduit ?? null,
    p_prix_local: correction.tarifs?.prix_local ?? null,
    p_prix_local_reduit: correction.tarifs?.prix_local_reduit ?? null,
    p_changer_condition: 'tarif_condition_id' in correction,
    p_tarif_condition_id: correction.tarif_condition_id ?? null,
  });
  if (error) throw new ErreurDeProgrammation(error.message);
  return (data ?? []) as SessionCorrigee[];
}
